import FAQItems from "../components/FAQItems";

const TEXT = "#EDEDED";
const TEXT_DIM = "#a0a0a0";
const TEAL = "#4FCDC4";
const TEAL_DIM = "rgba(79,205,196,0.15)";
const CARD_BORDER = "rgba(255,255,255,0.04)";

function H2({ id, children }) {
  return <h2 id={id} style={{ fontFamily: "var(--display)", fontWeight: 700, fontSize: "clamp(22px, 3vw, 28px)", color: TEXT, margin: "48px 0 16px", letterSpacing: "-0.01em", lineHeight: 1.2 }}>{children}</h2>;
}
function H3({ children }) {
  return <h3 style={{ fontFamily: "var(--display)", fontWeight: 700, fontSize: 19, color: TEXT, margin: "32px 0 12px", letterSpacing: "-0.005em", lineHeight: 1.3 }}>{children}</h3>;
}
function P({ children }) {
  return <p style={{ fontFamily: "var(--body)", fontSize: 16, color: TEXT_DIM, lineHeight: 1.85, margin: "0 0 18px" }}>{children}</p>;
}
function Li({ children }) {
  return <li style={{ fontFamily: "var(--body)", fontSize: 16, color: TEXT_DIM, lineHeight: 1.8, marginBottom: 8 }}>{children}</li>;
}
function Strong({ children }) {
  return <strong style={{ color: TEXT, fontWeight: 700 }}>{children}</strong>;
}
function A({ href, children, external }) {
  const isExternal = external || href.startsWith("http");
  return <a href={href} {...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})} style={{ color: TEAL, textDecoration: "underline", fontWeight: 600 }}>{children}</a>;
}
function PullQuote({ children }) {
  return (
    <blockquote style={{ margin: "32px 0", padding: "20px 24px", borderLeft: `3px solid ${TEAL}`, fontFamily: "var(--display)", fontSize: 20, fontWeight: 600, color: TEXT, lineHeight: 1.4, fontStyle: "normal" }}>
      {children}
    </blockquote>
  );
}

/* ── Visual 1: the two levers that compound ── */
function TwoLevers() {
  const levers = [
    { tag: "System level", t: "Earned citations", d: "Google's models decide your page is a good source, for everyone. You earn it with AEO." },
    { tag: "User level", t: "Preferred selections", d: "An individual reader tells Google to favor you. You earn it with audience trust." },
  ];
  return (
    <div style={{ background: "#0f0f11", borderRadius: 12, border: `1px solid ${CARD_BORDER}`, padding: 24, margin: "16px 0 32px" }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: TEXT, marginBottom: 4, fontFamily: "var(--body)" }}>Two levers into the same AI answers</div>
      <div style={{ fontSize: 10, color: TEXT_DIM, marginBottom: 20, fontFamily: "var(--body)" }}>They are earned differently, and they compound.</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}>
        {levers.map((l) => (
          <div key={l.t} style={{ padding: 16, borderRadius: 10, background: "rgba(255,255,255,0.03)", border: `1px solid ${CARD_BORDER}` }}>
            <div style={{ display: "inline-block", padding: "2px 8px", borderRadius: 6, background: TEAL_DIM, color: TEAL, fontFamily: "var(--mono)", fontSize: 10, fontWeight: 700, marginBottom: 10 }}>{l.tag}</div>
            <div style={{ fontFamily: "var(--display)", fontSize: 16, fontWeight: 700, color: TEXT, marginBottom: 6 }}>{l.t}</div>
            <div style={{ fontSize: 13, color: TEXT_DIM, lineHeight: 1.55 }}>{l.d}</div>
          </div>
        ))}
      </div>
      <div style={{ textAlign: "center", marginTop: 16, fontFamily: "var(--body)", fontSize: 13, color: TEAL, fontWeight: 700 }}>
        ↓ More of your brand in Top Stories, AI Overviews, and AI Mode ↓
      </div>
    </div>
  );
}

/* ── Visual 2: the integration stack ── */
function IntegrationStack() {
  const layers = [
    { k: "1", t: "Earn citations at the system level", d: "Structure your content so Google's AI chooses you for everyone." },
    { k: "2", t: "Become eligible and prompt your audience", d: "Make your site selectable, then ask the readers who already trust you." },
    { k: "3", t: "Measure across the AI surfaces", d: "Track your presence in Top Stories, AI Overviews, and AI Mode over time." },
  ];
  return (
    <div style={{ background: "#0f0f11", borderRadius: 12, border: `1px solid ${CARD_BORDER}`, padding: 24, margin: "16px 0 32px" }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: TEXT, marginBottom: 4, fontFamily: "var(--body)" }}>The AI-visibility stack</div>
      <div style={{ fontSize: 10, color: TEXT_DIM, marginBottom: 20, fontFamily: "var(--body)" }}>Preferred Sources slots in as layer 2, between earning and measuring.</div>
      {layers.map((l) => (
        <div key={l.k} style={{ display: "grid", gridTemplateColumns: "32px 1fr", gap: 14, alignItems: "start", padding: "12px 0", borderTop: `1px solid ${CARD_BORDER}` }}>
          <span style={{ width: 28, height: 28, borderRadius: 999, background: TEAL_DIM, color: TEAL, fontFamily: "var(--mono)", fontSize: 14, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{l.k}</span>
          <div>
            <div style={{ fontFamily: "var(--display)", fontSize: 15, fontWeight: 700, color: TEXT, marginBottom: 4 }}>{l.t}</div>
            <div style={{ fontSize: 13, color: TEXT_DIM, lineHeight: 1.55 }}>{l.d}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

const faqs = [
  {
    q: "Why does Google Preferred Sources matter for AI visibility?",
    a: "Because it is the first lever where your audience, not Google's algorithm, decides whether you show up. When a reader selects your site as a preferred source, your content surfaces more often, with a preferred badge, in their Top Stories, AI Overviews, and AI Mode. For brands that have cultivated an audience, it turns existing trust into AI search visibility without buying media or out-ranking anyone.",
  },
  {
    q: "How is Preferred Sources different from SEO or AEO?",
    a: "SEO and answer engine optimization are system-level: you optimize your content and Google's algorithm decides whether to show you, to everyone. Preferred Sources is user-level: an individual reader tells Google to favor you, and Google honors that preference for that person. SEO and AEO earn you the baseline; Preferred Sources layers a personalized boost on top for your most engaged readers. They are complementary, not competing.",
  },
  {
    q: "Does Preferred Sources replace getting cited by AI Overviews?",
    a: "No. Earning organic citations is still the foundation, because it reaches every searcher, not just the ones who selected you. Preferred Sources compounds with it: earned citations build the broad baseline, and preferred-source selections add a personalized lift for your audience. The strongest strategy runs both at once.",
  },
  {
    q: "Why should B2B brands act on Preferred Sources now?",
    a: "Most coverage frames Preferred Sources as a news or publisher feature, so the B2B field is wide open. The brands with an engaged newsletter, customer base, or community can start prompting selections before competitors realize it is a lever. The audience you already have is the moat, and acting early compounds while attention is low.",
  },
  {
    q: "What is the first step to using Preferred Sources strategically?",
    a: "Confirm your site is eligible (domain or subdomain level, not a subdirectory), then get your preferred-source deeplink and add it to the channels where your audience already engages. Pair that with your existing work to earn organic AI citations, so you are pursuing both the system-level and user-level levers at the same time.",
  },
  {
    q: "Can Preferred Sources help if I have a small audience?",
    a: "The eligibility groundwork helps regardless of audience size, but the lever is only as strong as the audience you can prompt. A small, highly engaged audience can still drive meaningful selections in a focused niche. If you have almost no audience yet, prioritize earning organic citations and building that audience first, then layer Preferred Sources on as it grows.",
  },
];

function Content() {
  return (
    <article style={{ maxWidth: 720, margin: "0 auto" }}>
      {/* Overview box */}
      <div style={{ background: "rgba(255,255,255,0.03)", border: `1px solid ${CARD_BORDER}`, borderRadius: 12, padding: "20px 24px", marginBottom: 32 }} id="quick-answer">
        <P>
          <Strong>Google Preferred Sources matters because it is the first AI-visibility lever your audience controls, not the algorithm.</Strong> When readers <A href="/blog/what-is-google-preferred-sources">select your site as a preferred source</A>, your content surfaces more often in their Top Stories, AI Overviews, and AI Mode. That turns the trust you have already earned into AI search visibility, it compounds with your organic citation work rather than replacing it, and because most of the market still treats it as a news feature, B2B brands have a real early-mover edge.
        </P>
      </div>

      {/* §1 */}
      <H2 id="the-shift">The shift: your readers now curate their own AI sources</H2>
      <P>
        For as long as search has existed, your visibility has been the algorithm&#39;s decision. You optimized, and Google decided whether to show you, to everyone, the same way. Preferred Sources changes that. For the first time, an individual reader can tell Google which sources to favor in its AI answers, and Google honors that choice for that person.
      </P>
      <P>
        And readers use it. During its Labs test, <A href="https://blog.google/products/search/preferred-sources/" external>Google reported</A> that over half of users chose four or more preferred sources. That is the signal that matters: given the option to curate the sources behind their AI answers, people take it. The surfaces this now touches, Top Stories, AI Overviews, and AI Mode, are exactly where your buyers increasingly get their answers.
      </P>
      <PullQuote>
        Search visibility used to be the algorithm&#39;s decision. Preferred Sources hands part of that decision to your reader.
      </PullQuote>

      {/* §2 */}
      <H2 id="why-it-compounds">Why it compounds with your AEO work</H2>
      <P>
        Preferred Sources does not compete with <A href="/blog/how-to-get-cited-by-google-ai-overviews">earning organic AI citations</A>. It compounds with it. Think of two separate doors into the same AI answer, earned at different levels.
      </P>

      <TwoLevers />

      <P>
        Earned citations are won at the system level: your content is strong enough that Google&#39;s models choose you for everyone. That builds the broad baseline. Preferred selections are won at the user level: a reader who trusts you tells Google to favor you, adding a personalized boost for your most engaged audience on top of that baseline. One reaches everyone shallowly; the other reaches your people deeply. Running both is strictly better than running either alone.
      </P>

      {/* §3 */}
      <H2 id="early-mover">The B2B early-mover case</H2>
      <P>
        Here is the opening. Almost all of the coverage of Preferred Sources frames it as a tool for news publishers, because that is how it launched. The eligibility rule carries no such restriction, but the framing has stuck, which means the B2B field is largely empty. The brands that recognize it as an AI-visibility lever first get to build a habit before their category is crowded.
      </P>
      <P>
        The advantage is also structural. Preferred Sources cannot be bought, so a better-funded competitor cannot simply outspend you into the badge. The only currency is audience trust, and that is the one asset a focused B2B brand can build deliberately. If you have a newsletter, a customer base, or a community, you already hold the input that matters. Acting now compounds while attention is low.
      </P>

      {/* §4 */}
      <H2 id="how-to-integrate">How to fold it into your AI-visibility strategy</H2>
      <P>
        You do not need a new program for this. Preferred Sources slots into the AI-visibility stack you should already be building, as the middle layer between earning citations and measuring them.
      </P>

      <IntegrationStack />

      <P>
        Layer one is the work you may already be doing: structure your content to <A href="/blog/how-to-get-cited-by-google-ai-overviews">be cited by Google&#39;s AI</A> at the system level. Layer two is the new lever: make your site eligible and <A href="/blog/how-to-become-a-google-preferred-source">prompt your audience to select you</A>, converting trust into a personalized boost. Layer three keeps you honest: because Google does not expose a count of who selected you, track your presence across Top Stories, AI Overviews, and AI Mode over time and watch for lift among the audiences you prompted. Tools that monitor those surfaces, including <A href="/platform/aeo">the AI-Advisors platform</A>, let you see the movement even without a first-party selection number.
      </P>
      <P>
        None of these layers is heavy on its own. The point is to run them together, so your system-level and user-level visibility build at the same time instead of one waiting on the other.
      </P>

      {/* FAQPage JSON-LD */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs.map((faq) => ({
          "@type": "Question",
          "name": faq.q,
          "acceptedAnswer": { "@type": "Answer", "text": faq.a },
        })),
      }) }} />

      {/* FAQ */}
      <H2 id="faq">Frequently Asked Questions</H2>
      <FAQItems faqs={faqs} />

      {/* Related Reading */}
      <H2 id="related">Related Reading</H2>
      <ul style={{ paddingLeft: 20, margin: "0 0 18px" }}>
        <Li><A href="/blog/what-is-google-preferred-sources">What Is Google Preferred Sources? The New AI Visibility Lever</A></Li>
        <Li><A href="/blog/how-to-become-a-google-preferred-source">How to Become a Google Preferred Source: A B2B Playbook</A></Li>
        <Li><A href="/blog/how-to-get-cited-by-google-ai-overviews">How to Get Cited by Google AI Overviews</A></Li>
        <Li><A href="/blog/google-ai-mode-vs-ai-overviews">Google AI Mode vs Google AI Overviews: What&#39;s the Difference?</A></Li>
      </ul>
    </article>
  );
}

const post = {
  slug: "why-google-preferred-sources-matter",
  title: "Why Google Preferred Sources Is Your Newest AI-Visibility Lever",
  seoTitle: "Why Google Preferred Sources Matter for AI Visibility",
  excerpt: "For the first time, your readers can tell Google which sources to favor in its AI answers. Here is why Google Preferred Sources is a user-controlled visibility lever that compounds with your organic AEO work, and why B2B brands have an early-mover edge.",
  metaDescription: "Google Preferred Sources is the first AI-visibility lever your audience controls, not the algorithm. Why it compounds with AEO and why B2B should act now.",
  date: "2026-06-05",
  readTime: "7 min read",
  category: "Answer Engine Optimization",
  tags: ["Google Preferred Sources", "AI visibility", "Google AI Overviews", "AI Mode", "answer engine optimization", "B2B marketing"],
  Content,
};

export default post;
